import { useCallback, useEffect, useRef, useState } from 'react';
import apiClient from '../api/client';
import { apiError } from './format';

/**
 * GET a dashboard endpoint (a falsy url means "not yet": nothing is fetched). `params` is part of the cache key, so changing a filter refetches.
 * Returns { data, loading, error, reload }. Stale responses from an earlier key are ignored.
 */
export default function useFetch(url, params) {
  const [state, setState] = useState({ data: null, loading: true, error: '' });
  const key = JSON.stringify([url, params || null]);
  const latest = useRef(key);

  const run = useCallback(async () => {
    latest.current = key;
    if (!url) {
      setState({ data: null, loading: false, error: '' });
      return;
    }
    setState((s) => ({ ...s, loading: true, error: '' }));
    try {
      const res = await apiClient.get(url, { params });
      if (latest.current === key) setState({ data: res.data, loading: false, error: '' });
    } catch (err) {
      if (latest.current === key) setState((s) => ({ data: s.data, loading: false, error: apiError(err, 'Could not load this page') }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Table cells keep the handlers they were drawn with, so `reload` must not change identity.
  const runRef = useRef(run);
  runRef.current = run;
  const reload = useCallback(() => runRef.current(), []);

  useEffect(() => {
    run();
  }, [run]);

  return { ...state, reload };
}
